import _ from 'lodash';
import Nexmo, { NXMCall, NXMEvent, Application, Member, ConversationMemberInfo } from 'nexmo-client';

import { IVoiceEvents } from '@/types';
import { resolveWithTimeout } from '@/common';

export class NexmoClient {
  public static readonly NXM_EVENTS = {
    INCOMING_CALL: 'member:call',
    MEMBER_JOIN: 'member:joined',
    MEMBER_LEAVE: 'member:left',
    ALREADY_JOINED: 'conversation:error:member-already-joined',
    TOKEN_EXPIRED: 'system:error:expired-token',
    RECONNECT_FAILED: 'error:client:reconnection_failed',
    NEXMO_ERROR_EVENT: 'NXM-errors',
  };

  /* Values are in milliseconds */
  public static readonly TOKEN_CACHE_TTL = 30 * 60 * 1000;
  public static readonly CHECK_NEXMO_SESSION_RATE = 3 * 60 * 60 * 1000;
  public static readonly CHECK_SESSION_TIMEOUT = 5 * 1000;

  /* Nexmo features used during calls */
  public static client: Nexmo;
  public static isDebug = false;
  protected static app?: Application;
  protected static call?: NXMCall;
  protected static nexmoTokenCache = { time: 0, token: '' };

  protected static isCheckingSession = false;
  protected static checkSessionTimeout: number;
  protected static checkSessionInterval: number;

  /** These values are provided by Action Desk */
  public static gatewayUrl: (key: string, accountId: string) => string;
  public static providersAccountId: string;
  public static authToken: string;
  public static handlers: IVoiceEvents;

  public static async initialize() {
    this.isDebug = JSON.parse(localStorage.getItem('HITL-Voice-Debug') || 'false');
    const token = await this.getToken();

    if (!token) {
      throw new Error('Nexmo JWT token is not available!');
    }

    this.client = this.client || new Nexmo({ debug: true });
    await Promise.race([
      (async () => {
        this.app = await this.client.createSession(token);
        await this.initializeApp();
      })(),
      resolveWithTimeout(10000),
    ]);
  }

  public static async reset() {
    if (!this.client) {
      return;
    }

    this.debug('Re-initializing...');
    await Promise.race([this.client.deleteSession(), resolveWithTimeout(10000)]);
    await this.initialize();
    this.debug('Re-initialized');
  }

  /** Assuming client is authorized, connect to Nexmo app features */
  protected static async initializeApp() {
    if (!this.app) {
      return;
    }

    const {
      INCOMING_CALL,
      ALREADY_JOINED,
      TOKEN_EXPIRED,
      RECONNECT_FAILED,
      NEXMO_ERROR_EVENT,
    } = this.NXM_EVENTS;

    this.app.on(INCOMING_CALL, (incoming: Member, call: NXMCall) => {
      this.handleIncomingCall({ incoming, call });
    });

    this.app.on(TOKEN_EXPIRED, NEXMO_ERROR_EVENT, () => this.updateToken());
    this.app.on(RECONNECT_FAILED, NEXMO_ERROR_EVENT, () => this.reset());
    this.app.on(ALREADY_JOINED, NEXMO_ERROR_EVENT, () =>
      this.handlers.onJoinCall()
    );

    if (this.isDebug) {
      this.app.on('*', (...args: any[]) => {
        this.debug('[App Event] *', ...args);
      });

      this.app.on('*', NEXMO_ERROR_EVENT, (...args: any) => {
        this.debug('[App Error Event] *', ...args);
      });
    }

    window.clearInterval(this.checkSessionInterval);
    this.checkSessionInterval = window.setInterval(() => {
      this.checkSession();
    }, this.CHECK_NEXMO_SESSION_RATE);
  }

  /** Add event handlers for an individual incoming call */
  protected static handleIncomingCall(args: {
    incoming: Member;
    call: NXMCall;
  }) {
    const { incoming, call } = args;
    this.call = call;
    this.debug('[App Event] - Incoming Call', incoming, call);

    const onJoin = (member: ConversationMemberInfo, event: NXMEvent) => {
      this.debug('[Call Event] - Member Joined', member, event);
      if (incoming.id === member.memberId) {
        this.handlers.onJoinCall();
        return;
      }

      const headers = _.get(event, ['body', 'channel', 'headers']);
      this.debug('[Call Event]- SIP Joined', { headers });
    };

    const onLeave = (member: ConversationMemberInfo, event: NXMEvent) => {
      const data = { member, event, sip: incoming.id !== member.memberId };
      this.debug('[Call Event] - Member Left', data);

      delete this.call;
      this.handlers.onLeaveCall();
    };

    call.conversation.on(this.NXM_EVENTS.MEMBER_JOIN, onJoin);
    call.conversation.on(this.NXM_EVENTS.MEMBER_LEAVE, onLeave);

    const botPhone = incoming.channel.from.number;
    let number = '';

    /** TODO: Find member that isn't a bot? */
    for (const member of call.to.values()) {
      if (member.user?.id !== this.app?.me.id) {
        const isBotPhone = member.channel.from.number === botPhone;
        number = isBotPhone
          ? member.channel.to.number
          : member.channel.from.number;
      }
    }

    this.handlers.onIncomingCall({ number, call });
  }

  /** Check client state for token expiration or disconnect */
  protected static async checkSession() {
    this.debug('Checking session...', { isChecking: this.isCheckingSession });
    if (this.isCheckingSession) {
      return;
    }

    this.isCheckingSession = true;
    this.checkSessionTimeout = window.setTimeout(() => {
      /** TODO: Maybe we need to throw an error? */
      this.isCheckingSession = false;
      this.debug('Session check timeout!');
    }, this.CHECK_SESSION_TIMEOUT);

    try {
      let message = '';

      if (!this.client.sessionReady || !this.client.connection || !this.app) {
        message = 'Client not ready - resetting...';
        await this.reset();
      } else {
        message = 'Client ready - updating token...';
        await this.updateToken();
      }

      this.debug(message, {
        app: !!this.app,
        sessionReady: this.client.sessionReady,
        connected: this.client.connection.connected,
      });

      this.isCheckingSession = false;
      window.clearTimeout(this.checkSessionTimeout);
    } catch (e) {
      this.debug('Error while checking session!');
      console.error(e);

      this.isCheckingSession = false;
      window.clearTimeout(this.checkSessionTimeout);
    }
  }

  protected static async updateToken() {
    const token = await this.getToken();
    if (!token) {
      throw new Error('Nexmo JWT token is not available!');
    }

    try {
      await this.app!.updateToken(token);
      this.client.connect();
    } catch (e) {
      this.debug('Error while updating Nexmo token!');
      console.error(e);
      await this.reset();
    }
  }

  /** Get token from cache or request a new one */
  protected static async getToken() {
    if (this.nexmoTokenCache.token) {
      const delta = Date.now() - this.nexmoTokenCache.time;
      const isExpired = delta >= this.TOKEN_CACHE_TTL;

      if (!isExpired) {
        return this.nexmoTokenCache.token;
      }
    }

    const accountId = this.providersAccountId;
    const url = this.gatewayUrl('get-nexmo-voice-user-gwt', accountId);
    const headers = { Authorization: `USER ${this.authToken}` };

    const request = new Request(url, { headers });
    const response = await fetch(request);

    /** TODO: Maybe we should throw an error? */
    if (!response.ok) {
      this.debug(`Error while requesting token! - ${response.statusText}`);
      return;
    }

    const content = await response.json();
    if (content.token) {
      this.nexmoTokenCache.time = Date.now();
      this.nexmoTokenCache.token = content.token;
      return content.token;
    }

    this.debug('Response doesn\'t have a token!');
  }

  protected static debug(message: string, ...args: any[]) {
    if (this.isDebug) {
      const time = new Date().toLocaleTimeString();
      console.debug(`[Nexmo Client - ${time}] ${message}`, ...args);
    }
  }
}
